import {showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {useAddMinipurchaseOfferedVehicleMutation} from '@dms/api/finance';
import {useCreateBusinessCaseMutation, useLinkTradeInMutation} from '@dms/api/sales';
import {useFormNavigate} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {businessCaseRoutes} from '@dms/routes';
import {handleApiError, queryParams} from '@dms/shared';

import {composePath, isNotNilOrEmpty, useNavigate} from 'shared/web';

import {useCreatePurchaseVehicle} from './useCreatePurchaseVehicle';
import {useCreateSaleVehicleForBusinessCase} from './useCreateSaleVehicleForBusinessCase';

/**
 * - Business case purchase (trade-in): link the created vehicle to the business case as a
 *   purchase offer, then return to the referrer.
 * - Business case sale from an existing business case (referrer carries the business case
 *   id): attach the created vehicle to that business case as its sale vehicle, then return
 *   to the referrer (the business case selling section).
 * - Business case sale from the new business case concept (no business case id): return to
 *   the business case concept with the created vehicle pre-selected. The concept then
 *   creates a Stock Sale business case once a customer is chosen, or keeps the vehicle in
 *   the selling widget when no customer is set.
 * - Interest: attach the created vehicle as an offered vehicle of the minipurchase, then
 *   return to the interest.
 * - No referrer: navigate to the created vehicle detail (default).
 */
export const useCreatedVehicleRedirect = () => {
  const {
    isBusinessCasePurchaseReferrer,
    isBusinessCaseSaleReferrer,
    isInterestReferrer,
    isBusinessCaseTradeInReferrer,
    getReferrerId,
    navigateOnConfirm,
    tradeInCustomerId,
    tradeInBranchId,
  } = useFormNavigate();

  const navigate = useNavigate();

  const {attachToBusinessCase} = useCreatePurchaseVehicle();
  const {attachSaleVehicleToBusinessCase} = useCreateSaleVehicleForBusinessCase();
  const [addMinipurchaseOfferedVehicle] = useAddMinipurchaseOfferedVehicleMutation();
  const [createBusinessCase] = useCreateBusinessCaseMutation();
  const [linkTradeIn] = useLinkTradeInMutation();

  const createdVehicleRedirect = (createdVehicleId: string) =>
    match({
      isBusinessCaseTradeInReferrer,
      isBusinessCasePurchaseReferrer,
      isBusinessCaseSaleReferrer,
      isInterestReferrer,
      hasBusinessCaseId: isNotNilOrEmpty(getReferrerId()),
    })
      .with({isBusinessCaseTradeInReferrer: true}, () => {
        if (isNil(tradeInCustomerId)) {
          navigateOnConfirm(createdVehicleId);
          return;
        }
        createBusinessCase({
          xBranch: tradeInBranchId ?? undefined,
          createBusinessCaseRequestBody: {
            customerId: tradeInCustomerId,
            vehicleToPurchaseId: createdVehicleId,
          },
        })
          .unwrap()
          .then((newBc) => {
            const navigateToCreatedBusinessCase = () =>
              navigate(composePath(businessCaseRoutes.buying, {params: {id: newBc.id}}));

            const originBusinessCaseId = getReferrerId();
            if (!originBusinessCaseId) {
              navigateToCreatedBusinessCase();
              return;
            }
            // The BC is already created at this point; whether or not the trade-in link
            // succeeds we navigate to it, so the user is never left on the form with an
            // orphaned, unreachable business case.
            return linkTradeIn({
              businessCaseId: originBusinessCaseId,
              linkTradeInRequestBody: {counterpartBusinessCaseId: newBc.id},
              xBranch: tradeInBranchId ?? undefined,
            })
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('page.businessCase.tradeIn.linkSuccess'));
                navigateToCreatedBusinessCase();
              })
              .catch(() => {
                showNotification.error(
                  i18n.t('page.businessCase.tradeIn.linkFailedBusinessCaseCreated')
                );
                navigateToCreatedBusinessCase();
              });
          })
          .catch(handleApiError);
      })
      .with({isBusinessCasePurchaseReferrer: true}, async () => {
        await attachToBusinessCase(createdVehicleId);
        navigateOnConfirm(createdVehicleId);
      })
      .with({isBusinessCaseSaleReferrer: true, hasBusinessCaseId: true}, async () => {
        await attachSaleVehicleToBusinessCase(createdVehicleId);
        navigateOnConfirm(createdVehicleId);
      })
      .with({isBusinessCaseSaleReferrer: true}, () => {
        navigate(
          composePath(businessCaseRoutes.concept, {
            queryParams: {[queryParams.VEHICLE_ID]: createdVehicleId},
          })
        );
      })
      .with({isInterestReferrer: true}, async () => {
        const minipurchaseId = getReferrerId();
        if (minipurchaseId) {
          await addMinipurchaseOfferedVehicle({
            minipurchaseId,
            addMinipurchaseOfferedVehicleRequestBody: {vehicleId: createdVehicleId},
          }).unwrap();
        }
        navigateOnConfirm(createdVehicleId);
      })
      .otherwise(() => {
        navigateOnConfirm(createdVehicleId);
      });

  return {createdVehicleRedirect};
};
